import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Lock, Mail, ArrowRight } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required')
});

type LoginFormValues = z.infer<typeof loginSchema>;

export const Login: React.FC = () => {
  const { login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema)
  });

  const onSubmit = async (data: LoginFormValues) => {
    setSubmitting(true);
    try {
      await login(data.email, data.password);
      showToast('Welcome back! Successfully logged in.', 'success');
      navigate('/dashboard');
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Invalid email or password';
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="space-y-1 mb-6">
        <h2 className="text-xl font-bold text-slate-900 uppercase tracking-wide">Sign In</h2>
        <p className="text-sm text-slate-500">Enter your credentials to access your email campaign command panel.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email Field */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email Address</label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
              <Mail size={16} />
            </span>
            <input
              type="email"
              placeholder="name@company.com"
              {...register('email')}
              className={`form-input w-full pl-10 ${errors.email ? 'border-accent-rose focus:ring-accent-rose focus:border-accent-rose' : ''}`}
            />
          </div>
          {errors.email && <span className="text-xs text-accent-rose font-medium">{errors.email.message}</span>}
        </div>

        {/* Password Field */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Password</label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
              <Lock size={16} />
            </span>
            <input
              type="password"
              placeholder="••••••••"
              {...register('password')}
              className={`form-input w-full pl-10 ${errors.password ? 'border-accent-rose focus:ring-accent-rose focus:border-accent-rose' : ''}`}
            />
          </div>
          {errors.password && <span className="text-xs text-accent-rose font-medium">{errors.password.message}</span>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary w-full flex justify-center items-center gap-2 mt-2"
        >
          {submitting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>Sign In <ArrowRight size={16} /></>
          )}
        </button>
      </form>
    </div>
  );
};
export default Login;
